/**
 * URLSearchParams#toString() re-encodes every value. We only need to drop or add
 * `username` for OAuth → login without changing how `redirect_uri` etc. appear.
 * Used by Success + Authorize only.
 */

function pairParamName(pair) {
  const i = pair.indexOf('=');
  return i === -1 ? pair : pair.slice(0, i);
}

function decodedParamNameEquals(pair, expectedLower) {
  const name = pairParamName(pair);
  try {
    return (
      decodeURIComponent(name.replace(/\+/g, ' ')).toLowerCase() ===
      expectedLower
    );
  } catch {
    return name.toLowerCase() === expectedLower;
  }
}

/**
 * Returns the raw `name=value` segment for the first matching query param
 * (name match is case-insensitive), preserving the client’s encoding.
 */
export function extractRawQueryParamPair(rawSearch, paramNameLower) {
  if (!rawSearch || rawSearch === '?') return null;
  const q = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch;
  if (!q) return null;
  const parts = q.split('&');
  for (const pair of parts) {
    if (pair && decodedParamNameEquals(pair, paramNameLower)) {
      return pair;
    }
  }
  return null;
}

/**
 * True when `rawSearch` has a non-empty `username` query value (matches signup
 * SSO: `username=` with no value does not count).
 */
export function rawSearchHasNonEmptyUsername(rawSearch) {
  const pair = extractRawQueryParamPair(rawSearch || '', 'username');
  if (!pair || !pair.includes('=')) return false;
  const v = pair.slice(pair.indexOf('=') + 1);
  return v.length > 0;
}

export function stripParamNames(rawSearch, paramNames) {
  if (!rawSearch || rawSearch === '?') return '';
  const q = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch;
  if (!q) return '';
  const lower = paramNames.map((n) => String(n).toLowerCase());
  const parts = q.split('&').filter((pair) => {
    if (!pair) return false;
    return !lower.some((name) => decodedParamNameEquals(pair, name));
  });
  const s = parts.join('&');
  return s ? `?${s}` : '';
}

/** Remove `username` only; leave other query segments byte-for-byte. */
export function searchWithoutUsername(rawSearch) {
  return stripParamNames(rawSearch || '', ['username']);
}

/**
 * Encode a username for a query value: full encodeURIComponent, then restore
 * literal `@` (readable email in the bar; still safe for typical OAuth flows).
 */
export function encodeUsernameQueryValue(username) {
  return encodeURIComponent(String(username == null ? '' : username).trim()).replace(
    /%40/g,
    '@',
  );
}

/** Remove `username` if present, then append encoded username hint. */
export function searchWithUsernameHint(rawSearch, username) {
  const base = stripParamNames(rawSearch || '', ['username']);
  const enc = encodeUsernameQueryValue(username);
  const rest = base.startsWith('?') ? base.slice(1) : '';
  if (!rest) return `?username=${enc}`;
  return `?${rest}&username=${enc}`;
}
